/* eslint-disable indent */
import { ArtworkCardProps } from 'components/artwork/ArtworkCard'
import { schemaOrgUrls } from 'constants/'
import { Currencies } from 'constants/currencies'
import { Regions } from 'constants/regions'
import { Translate } from 'next-translate'
import { Artist } from 'utils/data'
import { pickDynamicTradesPrices, toDollars } from 'utils/dynamic-prices'
import { getArtistUrl, getArtworkUrl } from 'utils/urls'

export function buildArtistSchemaLd(artist: Artist) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Person',
    name: artist.name,
    url: `${process.env.NEXT_PUBLIC_SITE_URL}${artist.href}`,
    image: artist.profile?.url || artist.avatar?.url,
    birthDate: artist.dateOfBirth, // ISO format: "YYYY-MM-DD"
    nationality: artist.nationalities?.map(n => ({
      '@type': 'Country',
      name: n.name,
      alternateName: n.shortName,
      code: n.alpha3Code,
    })),
    description: artist.description,
    jobTitle: 'Artist',
    sameAs: [
      artist.socialLinks.website,
      artist.socialLinks.instagram,
      artist.socialLinks.email && `mailto:${artist.socialLinks.email}`,
    ].filter(Boolean),
  }
}

export function buildArtworksSchemaLds(
  artworks: ArtworkCardProps[],
  t: Translate
) {
  return artworks.map(artwork => {
    const features = []
    const prices = artwork.dynamicTrades
      ? pickDynamicTradesPrices({
          trades: artwork.dynamicTrades,
          userCurrency: Currencies.GBP,
          userRegion: Regions.UK,
        })
      : null

    if (artwork.includes?.authenticityCertificate) {
      features.push(t('single_artist:certificate_of_authenticity'))
    }
    if (artwork.includes?.originalPacking) {
      features.push(t('single_artist:original_packaging'))
    }
    if (artwork.features?.signed) {
      features.push(t('single_artist:signed'))
    }
    if (artwork.features?.dated) {
      features.push(t('single_artist:dated'))
    }
    if (artwork.features?.handFinished) {
      features.push(t('single_artist:hand_finished'))
    }
    if (artwork.features?.numbered) {
      features.push(t('single_artist:numbered'))
    }

    return {
      '@context': schemaOrgUrls.url,
      '@type': 'Product',
      name: artwork.artworkName,
      description: t('single_artist:original_limited_edition_art', {
        medium: artwork.medium?.toLowerCase(),
        artistName: artwork.artistName,
        year: artwork.releaseDate
          ? new Date(artwork.releaseDate).getFullYear()
          : undefined,
        features: `${features.join(', ')}${features.length ? '.' : ''}`,
      }),
      image: {
        '@type': 'ImageObject',
        url: artwork.src,
      },
      brand: {
        '@type': 'Person',
        name: artwork.artistName,
      },
      url: `${process.env.NEXT_PUBLIC_SITE_URL}${getArtworkUrl(
        artwork.artists || [],
        artwork.artworkName || '',
        artwork.id || 0
      )}`,
      offers: {
        '@type': 'Offer',
        priceCurrency: Currencies.GBP,
        price: prices?.lowestAskGbp
          ? String(toDollars(prices.lowestAskGbp))
          : undefined,
        availability: prices?.lowestAskGbp
          ? schemaOrgUrls.inStock
          : schemaOrgUrls.outOfStock,
      },
      additionalProperty: [
        artwork.medium && {
          '@type': 'PropertyValue',
          name: 'Medium',
          value: artwork.medium,
        },
        artwork.releaseDate && {
          '@type': 'PropertyValue',
          name: 'Release Year',
          value: artwork.releaseDate.toString(),
        },
        artwork.editionName && {
          '@type': 'PropertyValue',
          name: 'Edition Name',
          value: artwork.editionName,
        },
      ].filter(Boolean),
    }
  })
}

export function buildBreacrumbsSchemaLd(artist: Artist) {
  const siteUrl = process.env.NEXT_PUBLIC_SITE_URL

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: siteUrl,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Artists',
        item: `${siteUrl}/artists`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: artist.name,
        item: `${siteUrl}${getArtistUrl(artist.name, artist.id)}`,
      },
    ],
  }
}
